import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const headers = () => { const t = localStorage.getItem("cauldron_token"); return t ? { Authorization: `Bearer ${t}` } : {}; };

/**
 * Provider Courses editor — create courses, add lessons, publish. Pro only.
 */
export default function ProCourses() {
  const [courses, setCourses] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", priceUsd: 29.99 });
  const [editingCourse, setEditingCourse] = useState(null);
  const [lessonForm, setLessonForm] = useState({ title: "", body: "", videoUrl: "" });
  const [gated, setGated] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/providers/me/courses`, { headers: headers() });
      setCourses(r.data.courses || []);
    } catch (e) {
      if (e.response?.status === 403) setGated(true);
    }
  };
  useEffect(() => { load(); }, []);

  const createCourse = async () => {
    try {
      await axios.post(`${API}/courses`, form, { headers: headers() });
      toast.success("Draft created");
      setForm({ title: "", description: "", priceUsd: 29.99 });
      setCreating(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const publish = async (id) => {
    try {
      await axios.post(`${API}/courses/${id}/publish`, {}, { headers: headers() });
      toast.success("Published");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const addLesson = async (cid) => {
    if (!lessonForm.title.trim()) return;
    try {
      await axios.post(`${API}/courses/${cid}/lessons`, {
        ...lessonForm, order: (editingCourse?.lessons?.length || 0) + 1
      }, { headers: headers() });
      toast.success("Lesson added");
      setLessonForm({ title: "", body: "", videoUrl: "" });
      // reload course detail
      const r = await axios.get(`${API}/courses/${cid}`, { headers: headers() });
      setEditingCourse(r.data);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const removeLesson = async (cid, lid) => {
    try {
      await axios.delete(`${API}/courses/${cid}/lessons/${lid}`, { headers: headers() });
      const r = await axios.get(`${API}/courses/${cid}`, { headers: headers() });
      setEditingCourse(r.data);
      load();
    } catch { toast.error("Failed"); }
  };

  if (gated) {
    return <Layout><div className="max-w-2xl mx-auto p-8 text-center text-amber-200/70">
      <h1 className="text-2xl font-serif text-amber-100 mb-3">Courses are a Pro feature</h1>
      <a href="/pricing" className="text-amber-400 underline">Upgrade to Pro →</a>
    </div></Layout>;
  }

  if (editingCourse) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto p-6" data-testid="course-editor">
          <button className="text-amber-400 text-sm mb-4" onClick={() => setEditingCourse(null)} data-testid="back-to-list">← Back to courses</button>
          <h1 className="text-2xl font-serif text-amber-100">{editingCourse.title}</h1>
          <div className="text-sm text-amber-200/60 mb-6">
            {editingCourse.status === "draft" ? "Draft" : "Published"} · ${editingCourse.priceUsd.toFixed(2)} · {(editingCourse.lessons || []).length} lessons
            {editingCourse.status === "draft" && (
              <Button size="sm" className="ml-4 bg-amber-500 text-neutral-900" onClick={() => publish(editingCourse.id)} data-testid="publish-course">Publish</Button>
            )}
          </div>

          <div className="rounded-lg border border-neutral-800 bg-neutral-900/40 p-4 mb-6">
            <div className="text-xs text-amber-200/50 uppercase mb-3">Add lesson</div>
            <Input placeholder="Lesson title" value={lessonForm.title} onChange={e => setLessonForm({...lessonForm, title: e.target.value})} data-testid="lesson-title" />
            <Textarea className="mt-2" placeholder="Lesson body (text)" value={lessonForm.body} onChange={e => setLessonForm({...lessonForm, body: e.target.value})} rows={3} data-testid="lesson-body" />
            <Input className="mt-2" placeholder="Optional video URL" value={lessonForm.videoUrl} onChange={e => setLessonForm({...lessonForm, videoUrl: e.target.value})} data-testid="lesson-video" />
            <Button className="mt-3" onClick={() => addLesson(editingCourse.id)} data-testid="add-lesson">Add lesson</Button>
          </div>

          <ol className="space-y-2" data-testid="lesson-list">
            {(editingCourse.lessons || []).map((l, i) => (
              <li key={l.id} className="p-3 rounded-lg border border-neutral-800 bg-neutral-900/50 flex justify-between" data-testid={`lesson-${l.id}`}>
                <div>
                  <div className="text-amber-100">{i + 1}. {l.title}</div>
                  {l.body && <div className="text-xs text-amber-200/50 line-clamp-2 mt-1">{l.body.slice(0, 200)}</div>}
                </div>
                <button className="text-red-400 text-xs" onClick={() => removeLesson(editingCourse.id, l.id)} data-testid={`remove-lesson-${l.id}`}>Remove</button>
              </li>
            ))}
          </ol>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-6" data-testid="pro-courses">
        <div className="flex justify-between mb-6">
          <h1 className="text-3xl font-serif text-amber-100">Courses</h1>
          <Button onClick={() => setCreating(true)} data-testid="create-course">+ New course</Button>
        </div>
        {creating && (
          <div className="rounded-lg border border-amber-500/40 bg-neutral-900/60 p-4 mb-6" data-testid="new-course-form">
            <Input placeholder="Course title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="course-title" />
            <Textarea className="mt-2" placeholder="Description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3} data-testid="course-desc" />
            <Input className="mt-2" type="number" step="0.01" placeholder="Price USD" value={form.priceUsd} onChange={e => setForm({...form, priceUsd: parseFloat(e.target.value) || 0})} data-testid="course-price" />
            <div className="flex gap-2 mt-3">
              <Button onClick={createCourse} disabled={!form.title.trim()} data-testid="save-course">Create draft</Button>
              <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
            </div>
          </div>
        )}
        {courses.length === 0 && !creating ? (
          <div className="text-amber-200/60 py-12 text-center">
            No courses yet. Create your first draft above.
          </div>
        ) : (
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="course-list">
            {courses.map(c => (
              <li
                key={c.id}
                className="p-4 rounded-lg border border-neutral-800 bg-neutral-900/40 hover:bg-neutral-900/70 cursor-pointer transition"
                onClick={async () => {
                  const r = await axios.get(`${API}/courses/${c.id}`, { headers: headers() });
                  setEditingCourse(r.data);
                }}
                data-testid={`course-${c.id}`}
              >
                <div className="flex justify-between items-start">
                  <h3 className="text-amber-100 font-serif">{c.title}</h3>
                  <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full ${c.status === "published" ? "bg-emerald-900/40 text-emerald-300" : "bg-amber-900/30 text-amber-200"}`}>
                    {c.status}
                  </span>
                </div>
                <div className="text-xs text-amber-200/50 mt-2">
                  ${(c.priceUsd || 0).toFixed(2)} · {(c.lessons || []).length} lessons
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Layout>
  );
}
